// File: src/components/EventDetailsCard.jsx
import React, { useState, useMemo } from 'react';
import { FaCalendar, FaMapMarkerAlt, FaInfoCircle } from 'react-icons/fa';
import { LuChevronRight, LuChevronDown } from 'react-icons/lu';

const S = {
  container: {
    boxSizing: 'border-box',
    maxWidth: 680,
    width: '94%',
    margin: '12px auto 0',
    padding: '0 12px',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },

  dividerRow: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '.8rem',
    color: '#616161',
    fontWeight: 700,
    marginBottom: 18,
    letterSpacing: '.05em',
    textTransform: 'uppercase',
  },
  dividerLine: { flex: 1, height: 1, backgroundColor: '#eee' },

  summaryCard: {
    padding: 14,
    backgroundColor: '#fff',
    borderRadius: 12,
    border: '0.5px solid #eee',
    marginBottom: 18,
  },

  /* Header: [icon box] [title + venue] [chevron button] */
  headerRow: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto',
    gap: 10,
    alignItems: 'center',
    marginBottom: 8,
  },

  iconBox: {
    width: 38,
    height: 38,
    borderRadius: 10,
    display: 'grid',
    placeItems: 'center',
    background: '#fff',
    border: '0.5px solid #eee',
  },

  titleWrap: { minWidth: 0 },
  headline: {
    fontSize: 12.5,
    fontWeight: 800,
    color: '#111',
    margin: 0,
    letterSpacing: '-0.01em',
    lineHeight: 1.2,
  },
  subVenue: {
    margin: '3px 0 0',
    fontSize: 11,
    fontWeight: 500,
    lineHeight: 1.3,
    color: '#222',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  ArrowCircleBtn: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    border: '1px solid #ddd',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
    padding: 0,
    lineHeight: 0,
    color: '#111',
  },
  chevWrap: {
    width: 14,
    height: 14,
    display: 'grid',
    placeItems: 'center',
  },
  chevIcon: {
    display: 'block',
    width: '100%',
    height: '100%',
  },

  description: {
    fontSize: 11.5,
    color: '#475569',
    margin: '2px 0 10px',
    lineHeight: 1.5,
  },

  chipRow: {
    display: 'flex',
    gap: '0.5rem',
    overflowX: 'auto',
  },
  grayChip: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    color: '#111',
    fontSize: '.7rem',
    fontWeight: 600,
    padding: '6px 7px',
    borderRadius: 6,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
};

export default function EventDetailsCard({ event }) {
  const [showFullDesc, setShowFullDesc] = useState(false);

  const dateTimeStr = `${event.eventDate}, ${event.startTime12} – ${event.endTime12}`;
  const truncatedDesc =
    event.description?.length > 95
      ? `${event.description.slice(0, 95)}...`
      : event.description || '';

  const venue = useMemo(() => {
    return (
      event.hostedAt ||
      event.venue ||
      (typeof event.address === 'string' ? event.address.split(',')[0] : '') ||
      ''
    );
  }, [event]);

  return (
    <div style={S.container}>
      <div style={S.dividerRow}>
        <div style={S.dividerLine} />
        <div style={S.dividerLine} />
      </div>

      <div style={S.summaryCard}>
        {/* Header row */}
        <div style={S.headerRow}>
          <div style={S.iconBox} aria-hidden>
            <FaInfoCircle style={{ fontSize: 16, color: '#475569' }} />
          </div>

          <div style={S.titleWrap}>
            <p style={S.headline} title={event.name}>{event.name}</p>
            {venue ? (
              <div style={S.subVenue} title={venue}>{venue}</div>
            ) : null}
          </div>

          <button
            style={S.ArrowCircleBtn}
            onClick={() => setShowFullDesc((v) => !v)}
            aria-label={showFullDesc ? 'Show less' : 'Show more'}
            aria-expanded={showFullDesc}
            aria-controls="event-details-description"
          >
            <span style={S.chevWrap}>
              {showFullDesc
                ? <LuChevronDown size={14} style={S.chevIcon} />
                : <LuChevronRight size={14} style={S.chevIcon} />
              }
            </span>
          </button>
        </div>

        {/* Description */}
        <p id="event-details-description" style={S.description}>
          {showFullDesc ? (event.description || '') : truncatedDesc}
        </p>

        {/* Chips */}
        <div style={S.chipRow} className="hide-scrollbar">
          <div style={S.grayChip}>
            <FaCalendar style={{ marginRight: 6, fontSize: '.8rem' }} />
            {dateTimeStr}
          </div>
          {event.address && (
            <div style={S.grayChip}>
              <FaMapMarkerAlt style={{ marginRight: 6, fontSize: '.8rem' }} />
              {event.address}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
